// Platsagenter (owner 2026-10-30: the agent is the site's arm into the LAN, not a printer
// thing): every agent with version, last heartbeat, errors and log; enrolment with a one-time
// code; fetch-from-LAN tasks and the task list (VBS calls included). Printers stay on Skrivare.

import { useEffect, useState } from "react";
import { Admin, getSession, type AgentOut, type AgentTaskOut, type PrinterOut } from "../api/client.js";
import { t } from "../i18n.js";

const fmt = (iso: string | null) => (iso ? new Date(iso).toLocaleString("sv-SE", { dateStyle: "short", timeStyle: "short" }) : "—");

export function Agents() {
  const [agents, setAgents] = useState<AgentOut[]>([]);
  const [printers, setPrinters] = useState<PrinterOut[] | null>(null);
  const [tasks, setTasks] = useState<AgentTaskOut[]>([]);
  const [taskUrl, setTaskUrl] = useState("");
  const [taskHost, setTaskHost] = useState<string>("");
  const [showTask, setShowTask] = useState<string | null>(null);
  const [showLog, setShowLog] = useState<Record<string, boolean>>({});
  const [msg, setMsg] = useState<string | null>(null);
  // The agent's own credential (owner 2026-10-30): the office makes a code here, the agent
  // exchanges it on its machine (node agent.mjs enrol <code>); the integrator token leaves the PC.
  const [enrolHost, setEnrolHost] = useState<string | null>(null);
  const [enrolCode, setEnrolCode] = useState<{ host: string; code: string; expiresAt: string; reenrol: boolean } | null>(null);
  const makeCode = async (host: string) => {
    try {
      const r = await Admin.enrolAgent(host.trim());
      setEnrolCode({ host: r.host, code: r.enrolCode, expiresAt: r.expiresAt, reenrol: r.reenrol });
      setEnrolHost(null);
    } catch (e) {
      setEnrolCode(null);
      alert(e instanceof Error ? e.message : String(e));
    }
  };
  const load = () => {
    Admin.printAgents().then(setAgents).catch(() => setAgents([]));
    Admin.printers().then(setPrinters).catch(() => setPrinters([]));
    Admin.agentTasks().then(setTasks).catch(() => setTasks([]));
  };
  useEffect(() => {
    load();
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, []);
  useEffect(() => {
    if (!tasks.some((x) => x.state === "queued" || x.state === "running")) return;
    const id = setInterval(() => { Admin.agentTasks().then(setTasks).catch(() => undefined); }, 3000);
    return () => clearInterval(id);
  }, [tasks]);
  const recent = (iso: string | null) => !!iso && Date.now() - new Date(iso).getTime() < 5 * 60_000;
  const liveAgents = agents.filter((a) => recent(a.lastSeenAt));
  const hosts = [...new Set([...agents.map((a) => a.host), ...(printers ?? []).flatMap((p) => (p.agentHost ? [p.agentHost] : []))])].sort();

  return (
    <>
      <div className="adm-top">
        <div><h1>{t("adm.agents")}</h1><div className="crumb">{t("adm.agents.sub")} · {liveAgents.length > 0 ? liveAgents.map((a) => a.host).join(", ") : t("adm.agents.noneLive")}</div></div>
      </div>
      <div className="adm-content">
        {msg && <p className="ahint" style={{ color: "var(--ok)" }}>{msg}</p>}
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div style={{ display: "contents" }}>
            <div className="card">
              <div className="hd">{t("adm.prn.agents")} <span className="sub">{t("adm.prn.agents.sub")}</span></div>
              <div className="bd flush">
                <table className="at">
                  <thead><tr><th>{t("adm.prn.col.agentHost")}</th><th>{t("adm.prn.col.version")}</th><th>{t("adm.prn.col.lastSeen")}</th><th>{t("adm.col.status")}</th><th>{t("adm.prn.col.login")}</th><th>{t("adm.prn.col.lastError")}</th><th></th></tr></thead>
                  <tbody>
                    {agents.length === 0 && <tr><td colSpan={7} className="mut">{t("adm.prn.agents.none")}</td></tr>}
                    {agents.map((a) => {
                      const lastErr = [...a.log].reverse().find((l) => l.level === "error");
                      return (
                        <tr key={a.host}>
                          <td><b>{a.host}</b><div className="mut" style={{ fontSize: 12 }}>{a.platform ?? ""}</div></td><td className="id">{a.version ?? "—"}</td><td className="id mut">{fmt(a.lastSeenAt)}</td>
                          <td>{recent(a.lastSeenAt) ? <span className="pill ok">{t("adm.prn.agentOk")}</span> : <span className="pill warn">{t("adm.prn.agentGone", { at: fmt(a.lastSeenAt) })}</span>}</td>
                          <td>{a.enrolled ? <span className="pill ok">{t("adm.prn.login.own")}</span> : <span className="pill warn">{t("adm.prn.login.serverKey")}</span>}</td>
                          <td className="mut" style={{ fontSize: 13 }}>{lastErr ? <><span className="id">{fmt(lastErr.at)}</span> · {lastErr.message}</> : t("adm.prn.noErrors")}</td>
                          <td style={{ whiteSpace: "nowrap" }}><button type="button" className="abtn sm" onClick={() => void makeCode(a.host)}>{t("adm.prn.agent.newCode")}</button> {a.log.length > 0 && <button type="button" className="abtn sm" onClick={() => setShowLog({ ...showLog, [a.host]: !showLog[a.host] })}>{showLog[a.host] ? t("adm.prn.hideLog") : t("adm.prn.showLog")}</button>}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {agents.filter((a) => showLog[a.host]).map((a) => (
                  <pre key={a.host} style={{ margin: 0, padding: "10px 12px", borderTop: "1px solid var(--line)", fontSize: 12, fontFamily: "var(--mono)", whiteSpace: "pre-wrap", maxHeight: 320, overflow: "auto" }}>
                    {a.host}\n{[...a.log].reverse().map((l) => `${l.at.replace("T", " ").slice(0, 19)}  ${l.level === "error" ? "FEL " : "    "} ${l.message}`).join("\n")}
                  </pre>
                ))}
                <div style={{ borderTop: "1px solid var(--line)", padding: "10px 12px", display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                  {enrolHost === null ? (
                    <button type="button" className="abtn sm" onClick={() => setEnrolHost("")}>{t("adm.prn.agent.enrol")}</button>
                  ) : (
                    <form style={{ display: "flex", gap: 8, alignItems: "center" }} onSubmit={(e) => { e.preventDefault(); if (enrolHost.trim()) void makeCode(enrolHost); }}>
                      <input className="ain" style={{ height: 30, width: 200 }} value={enrolHost} onChange={(e) => setEnrolHost(e.target.value)} placeholder={t("adm.prn.agent.hostPlaceholder")} autoFocus />
                      <button type="submit" className="abtn sm primary" disabled={!enrolHost.trim()}>{t("adm.prn.agent.makeCode")}</button>
                      <button type="button" className="abtn sm" onClick={() => setEnrolHost(null)}>{t("common.cancel")}</button>
                    </form>
                  )}
                  {enrolCode && (
                    <span className="ahint" style={{ margin: 0 }}>
                      {t(enrolCode.reenrol ? "adm.prn.agent.codeReenrol" : "adm.prn.agent.code", { host: enrolCode.host, tenant: getSession()?.tenantName ?? "", until: new Date(enrolCode.expiresAt).toLocaleTimeString("sv-SE", { hour: "2-digit", minute: "2-digit" }) })}{" "}
                      <code style={{ fontFamily: "var(--mono)", fontSize: 14, fontWeight: 700 }}>{enrolCode.code}</code> · <code style={{ fontFamily: "var(--mono)", fontSize: 12 }}>node agent.mjs enrol {enrolCode.code}</code>
                    </span>
                  )}
                </div>
                <div style={{ borderTop: "1px solid var(--line)", padding: "10px 12px" }}>
                  <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                    <b style={{ fontSize: 13 }}>{t("adm.prn.fetch")}</b>
                    <input value={taskUrl} onChange={(e) => setTaskUrl(e.target.value)} placeholder={t("adm.prn.fetch.url")} style={{ flex: 1, minWidth: 260 }} />
                    <select value={taskHost} onChange={(e) => setTaskHost(e.target.value)}>
                      <option value="">{t("adm.prn.fetch.anyHost")}</option>
                      {hosts.map((h) => <option key={h} value={h}>{h}</option>)}
                    </select>
                    <button type="button" className="abtn sm primary" disabled={!/^https?:\/\//i.test(taskUrl.trim())} onClick={() => { Admin.createAgentTask({ host: taskHost || null, kind: "fetch", url: taskUrl.trim() }).then(() => { setTaskUrl(""); load(); }).catch((e) => setMsg(String(e))); }}>{t("adm.prn.fetch.go")}</button>
                  </div>
                  <div className="mut" style={{ fontSize: 12, marginTop: 4 }}>{t("adm.prn.fetch.sub")}</div>
                  {tasks.length > 0 && (
                    <table className="at" style={{ marginTop: 8 }}>
                      <thead><tr><th>{t("adm.col.time")}</th><th>URL</th><th>{t("adm.prn.col.agentHost")}</th><th>{t("adm.col.status")}</th><th></th></tr></thead>
                      <tbody>
                        {tasks.map((x) => (
                          <tr key={x.id}>
                            <td className="id mut">{fmt(x.createdAt)}</td>
                            <td className="id wrap">{"url" in x.request ? x.request.url : `VBS ${x.request.endpoint}`}</td>
                            <td className="mut">{x.host ?? t("adm.prn.fetch.anyHost")}</td>
                            <td>
                              {x.state === "done" ? <span className="pill ok">{x.result?.status} · {x.result ? `${Math.round(x.result.bytes / 1024)} kB` : ""}</span> : x.state === "failed" ? <span className="pill bad">{t("adm.prn.fetch.failed")}</span> : <span className="pill">{t("adm.prn.fetch.waiting")}</span>}
                              {x.error && <div className="mut" style={{ fontSize: 12 }}>{x.error}</div>}
                            </td>
                            <td style={{ whiteSpace: "nowrap" }}>
                              {x.result && <button type="button" className="abtn sm" onClick={() => setShowTask(showTask === x.id ? null : x.id)}>{showTask === x.id ? t("adm.prn.hideLog") : t("adm.prn.fetch.show")}</button>}{" "}
                              {x.result && <a className="abtn sm" download={`${("url" in x.request ? x.request.url : x.request.endpoint).replace(/[^a-z0-9.]+/gi, "_").slice(0, 80)}.txt`} href={`data:text/plain;charset=utf-8,${encodeURIComponent(x.result.text)}`}>{t("adm.prn.fetch.download")}</a>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                  {tasks.filter((x) => x.id === showTask && x.result).map((x) => (
                    <pre key={x.id} style={{ margin: "8px 0 0", padding: "10px 12px", border: "1px solid var(--line)", borderRadius: 8, fontSize: 12, fontFamily: "var(--mono)", whiteSpace: "pre-wrap", maxHeight: 420, overflow: "auto" }}>{x.result!.text}</pre>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
